import PropTypes from 'prop-types';
import React from 'react';
import styled, { useTheme } from 'styled-components';
import { formatTimestamp } from 'shared/formatters';
import MessagePreview from '../MessagePreview';
import {
  ReviewContainer,
  SummaryHeader,
  SummaryCustomer,
  CustomerAvatar,
  CustomerInfo,
  CustomerName,
  CustomerContact,
  SummaryAmount,
  AmountLabel,
  AmountValue,
  TwoColumnLayout,
  LeftColumn,
  SummarySection,
  SummaryList,
  SummaryItem,
  ItemLabel,
  ItemValue,
  ProcessingFeesItem,
  TicketList,
  TicketItem,
  TicketInfo,
  SerialNumber,
  TicketName,
  TicketPrice,
  TransactionDetails,
  TransactionInfo,
  TransactionLabel,
  TransactionValue,
  getInitials,
} from '../shared/ReviewStyledComponents';

// Custom styled component for the preview section with less padding
const PreviewSection = styled(SummarySection)`
  padding: 16px;
  height: fit-content;
`;

// Subsection header for nested sections within cards
const SubsectionHeader = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${(props) =>
    props.theme.name === 'dark' ? '#ffffff' : props.theme.colors.blueBlack};
  margin-bottom: 8px;
`;

// Helper function to get ticket/pass text based on platform
const getTicketText = (count = 1) => {
  const isTicketHoss = process.env.REACT_APP_PLATFORM === 'tickethoss';
  if (isTicketHoss) {
    return count === 1 ? 'ticket' : 'tickets';
  }
  return count === 1 ? 'pass' : 'passes';
};

// Helper function to get text based on adjustment type
const getAdjustmentText = (type, key) => {
  const texts = {
    credit: {
      amountLabel: 'Credit Amount',
      methodTitle: 'Credit Method',
      amountTitle: 'Credit Details',
      toIssue: 'Credit to Issue',
    },
    refund: {
      amountLabel: 'Refund Amount',
      methodTitle: 'Refund Method',
      amountTitle: 'Refund Details',
      toIssue: 'Refund to Issue',
    },
  };
  return texts[type]?.[key] || texts.credit[key];
};

const Step3Review = ({
  creditData,
  trackName = 'Your Track',
  transactionData,
  type = 'credit',
}) => {
  const theme = useTheme();

  // Determine if this is a mixed scenario (both credit restoration and refund)
  const isMixedScenario = () => {
    if (type !== 'refund') return false;
    const credit = parseFloat(creditData.creditRestored) || 0;
    const refund = parseFloat(creditData.dynamicRefundAmount) || 0;
    return credit > 0 && refund > 0;
  };

  const isFullCreditScenario = () => {
    if (type !== 'refund') return false;
    const credit = parseFloat(creditData.creditRestored) || 0;
    const refund = parseFloat(creditData.dynamicRefundAmount) || 0;
    return credit > 0 && refund === 0;
  };

  // Calculate credit amount
  const calculateCreditAmount = () => {
    if (creditData.creditMethod === 'custom') {
      return parseFloat(creditData.customAmount) || 0;
    }

    // For tickets method, use the amount from backend (dynamicRefundAmount)
    if (creditData.creditMethod === 'tickets') {
      return parseFloat(creditData.amount) || 0;
    }

    return 0;
  };

  const creditAmount = calculateCreditAmount();
  const mixedScenario = isMixedScenario();
  const fullCreditScenario = isFullCreditScenario();

  // Calculate new balance
  const newBalance =
    (parseFloat(creditData.currentBalance) || 0) + creditAmount;

  // Format expiration date
  const formatExpiration = () => {
    if (!creditData.expiration) return 'No expiration';
    const date = new Date(creditData.expiration);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Replace variables in message
  const replaceVariables = (text) => {
    if (!text) return text;

    // Handle anonymous customer
    const firstName =
      creditData.customerName === 'Anonymous Customer'
        ? 'Customer'
        : creditData.customerName.split(' ')[0];

    // Calculate the refund/credit amounts based on the method
    let refundAmount = 0;
    let creditRestoredAmount = 0;

    if (type === 'refund') {
      if (creditData.creditMethod === 'tickets') {
        // Ticket-based refund: use dynamic amounts from backend
        refundAmount = parseFloat(creditData.dynamicRefundAmount) || 0;
        creditRestoredAmount = parseFloat(creditData.creditRestored) || 0;
      } else if (creditData.creditMethod === 'custom') {
        // Custom amount refund: use customAmount directly
        refundAmount = parseFloat(creditData.customAmount) || 0;
        creditRestoredAmount = 0;
      }
    } else {
      // For credits
      if (creditData.creditMethod === 'custom') {
        creditRestoredAmount = parseFloat(creditData.customAmount) || 0;
      } else {
        creditRestoredAmount = parseFloat(creditData.amount) || 0;
      }
    }

    return text
      .replace(/\{\{first_name\}\}/g, firstName)
      .replace(/\{\{credit_amount\}\}/g, `$${creditRestoredAmount.toFixed(2)}`)
      .replace(/\{\{refund_amount\}\}/g, `$${refundAmount.toFixed(2)}`)
      .replace(/\{\{expiration\}\}/g, formatExpiration())
      .replace(/\{\{track_name\}\}/g, trackName);
  };

  // Calculate character count
  const characterCount = creditData.messageContent
    ? creditData.messageContent.length
    : 0;
  const characterLimit = creditData.messageChannel === 'SMS' ? 160 : 250;

  return (
    <ReviewContainer>
      <SummaryHeader>
        <SummaryCustomer>
          <CustomerAvatar>
            {getInitials(creditData.customerName)}
          </CustomerAvatar>
          <CustomerInfo>
            <CustomerName>{creditData.customerName}</CustomerName>
            {(creditData.customerEmail || creditData.customerPhone) && (
              <CustomerContact>
                {creditData.customerEmail}
                {creditData.customerEmail && creditData.customerPhone && ' • '}
                {creditData.customerPhone}
              </CustomerContact>
            )}
          </CustomerInfo>
        </SummaryCustomer>
        {mixedScenario ? (
          <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-end' }}>
            <SummaryAmount>
              <AmountLabel>Credit Restoration</AmountLabel>
              <AmountValue
                style={{ color: theme?.colors?.success || '#22C55E' }}
              >
                ${(parseFloat(creditData.creditRestored) || 0).toFixed(2)}
              </AmountValue>
            </SummaryAmount>
            <SummaryAmount>
              <AmountLabel>Card Refund</AmountLabel>
              <AmountValue
                style={{ color: theme?.colors?.primary || '#fa4616' }}
              >
                ${(parseFloat(creditData.dynamicRefundAmount) || 0).toFixed(2)}
              </AmountValue>
            </SummaryAmount>
          </div>
        ) : fullCreditScenario ? (
          <SummaryAmount>
            <AmountLabel>Credit Restoration</AmountLabel>
            <AmountValue>
              ${(parseFloat(creditData.creditRestored) || 0).toFixed(2)}
            </AmountValue>
          </SummaryAmount>
        ) : (
          <SummaryAmount>
            <AmountLabel>{getAdjustmentText(type, 'amountLabel')}</AmountLabel>
            <AmountValue>${creditAmount.toFixed(2)}</AmountValue>
          </SummaryAmount>
        )}
      </SummaryHeader>

      <TwoColumnLayout>
        <LeftColumn>
          {/* Transaction Details Section */}
          {transactionData && (
            <SummarySection
              title={
                <>
                  <svg
                    width="16"
                    height="16"
                    fill={theme?.colors?.primary || '#fa4616'}
                    viewBox="0 0 16 16"
                    style={{ marginRight: '8px' }}
                  >
                    <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4zm2-1a1 1 0 0 0-1 1v1h14V4a1 1 0 0 0-1-1H2zm13 4H1v5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V7z" />
                    <path d="M2 10a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v1a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-1z" />
                  </svg>
                  Transaction Information
                </>
              }
            >
              <TransactionDetails>
                <TransactionInfo>
                  <TransactionLabel>Transaction ID</TransactionLabel>
                  <TransactionValue>#{transactionData.id}</TransactionValue>
                </TransactionInfo>
                <TransactionInfo>
                  <TransactionLabel>Event</TransactionLabel>
                  <TransactionValue>
                    {transactionData.eventName}
                  </TransactionValue>
                </TransactionInfo>
                <TransactionInfo>
                  <TransactionLabel>Date</TransactionLabel>
                  <TransactionValue>
                    {transactionData.purchaseDate
                      ? formatTimestamp(
                          Number(transactionData.purchaseDate),
                          'MMM D, YYYY h:mm A',
                          true
                        )
                      : 'N/A'}
                  </TransactionValue>
                </TransactionInfo>
                <TransactionInfo>
                  <TransactionLabel>Original Total</TransactionLabel>
                  <TransactionValue>
                    $
                    {typeof transactionData.total === 'number'
                      ? transactionData.total.toFixed(2)
                      : transactionData.total}
                  </TransactionValue>
                </TransactionInfo>
              </TransactionDetails>

              {creditData.creditMethod === 'tickets' &&
                creditData.selectedTickets.length > 0 && (
                  <>
                    <SubsectionHeader
                      style={{
                        fontSize: '13px',
                        marginTop: '16px',
                        marginBottom: '8px',
                      }}
                    >
                      {getTicketText(creditData.selectedTickets.length)
                        .charAt(0)
                        .toUpperCase() +
                        getTicketText(creditData.selectedTickets.length).slice(
                          1
                        )}{' '}
                      to {type === 'refund' ? 'Refund' : 'Credit'} (
                      {creditData.selectedTickets.length})
                    </SubsectionHeader>
                    <TicketList>
                      {creditData.selectedTickets.map((ticket, index) => (
                        <TicketItem key={index}>
                          <TicketInfo>
                            <TicketName>{ticket.name}</TicketName>
                            {process.env.REACT_APP_PLATFORM === 'tickethoss' &&
                              ticket.serialNumber && (
                                <div
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    marginLeft: '8px',
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: '13px',
                                      color:
                                        theme?.name === 'dark'
                                          ? theme?.colors?.textGray
                                              ?.secondary || '#94A3B8'
                                          : '#64748B',
                                      fontWeight: 500,
                                    }}
                                  >
                                    {ticket.serialNumber.slice(0, -4)}
                                  </span>
                                  <SerialNumber>
                                    {ticket.serialNumber.slice(-4)}
                                  </SerialNumber>
                                </div>
                              )}
                          </TicketInfo>
                          <TicketPrice>${ticket.price}</TicketPrice>
                        </TicketItem>
                      ))}
                    </TicketList>
                    <ProcessingFeesItem>
                      <ItemLabel>Processing Fees</ItemLabel>
                      <ItemValue>
                        {creditData.includeProcessingFees
                          ? `Included ($${
                              creditData.dynamicProcessingFees?.toFixed(2) ||
                              '0.00'
                            })`
                          : 'Not Included'}
                      </ItemValue>
                    </ProcessingFeesItem>
                  </>
                )}

              {creditData.creditMethod === 'custom' && (
                <SummaryItem style={{ marginTop: '12px' }}>
                  <ItemLabel>
                    {type === 'refund' ? 'Refund' : 'Credit'} Type
                  </ItemLabel>
                  <ItemValue>
                    {type === 'refund'
                      ? `Partial Refund (${
                          getTicketText(2).charAt(0).toUpperCase() +
                          getTicketText(2).slice(1)
                        } remain valid)`
                      : `Partial Credit (${
                          getTicketText(2).charAt(0).toUpperCase() +
                          getTicketText(2).slice(1)
                        } remain valid)`}
                  </ItemValue>
                </SummaryItem>
              )}
            </SummarySection>
          )}

          {type === 'credit' && (
            <SummarySection
              title={
                <>
                  <svg
                    width="16"
                    height="16"
                    fill={theme?.colors?.primary || '#fa4616'}
                    viewBox="0 0 16 16"
                    style={{ marginRight: '8px' }}
                  >
                    <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v5H0V4zm0 6v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2H0zm3 1a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2z" />
                  </svg>
                  Credit Information
                </>
              }
            >
              <SummaryList>
                <SummaryItem>
                  <ItemLabel>Current Balance</ItemLabel>
                  <ItemValue>${creditData.currentBalance || '0.00'}</ItemValue>
                </SummaryItem>
                <SummaryItem>
                  <ItemLabel>Credit to Issue</ItemLabel>
                  <ItemValue
                    style={{ color: theme?.colors?.success || '#22C55E' }}
                  >
                    +${creditAmount.toFixed(2)}
                  </ItemValue>
                </SummaryItem>
                <SummaryItem>
                  <ItemLabel>New Balance</ItemLabel>
                  <ItemValue
                    style={{
                      color: theme?.colors?.success || '#22C55E',
                      fontWeight: 700,
                    }}
                  >
                    ${newBalance.toFixed(2)}
                  </ItemValue>
                </SummaryItem>
                <SummaryItem>
                  <ItemLabel>Expiration Date</ItemLabel>
                  <ItemValue>{formatExpiration()}</ItemValue>
                </SummaryItem>
                {creditData.reason && (
                  <SummaryItem>
                    <ItemLabel>Reason</ItemLabel>
                    <ItemValue>{creditData.reason}</ItemValue>
                  </SummaryItem>
                )}
              </SummaryList>
            </SummarySection>
          )}

          {creditData.sendMessage && (
            <SummarySection
              title={
                <>
                  <svg
                    width="16"
                    height="16"
                    fill={theme?.colors?.primary || '#fa4616'}
                    viewBox="0 0 16 16"
                    style={{ marginRight: '8px' }}
                  >
                    <path d="M0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.5a1 1 0 0 0-.8.4l-1.9 2.533a1 1 0 0 1-1.6 0L5.3 12.4a1 1 0 0 0-.8-.4H2a2 2 0 0 1-2-2V2zm3.5 1a.5.5 0 0 0 0 1h9a.5.5 0 0 0 0-1h-9zm0 2.5a.5.5 0 0 0 0 1h9a.5.5 0 0 0 0-1h-9zm0 2.5a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1h-5z" />
                  </svg>
                  Customer Notification
                </>
              }
            >
              <SummaryList>
                <SummaryItem>
                  <ItemLabel>Send Via</ItemLabel>
                  <ItemValue>{creditData.messageChannel} Message</ItemValue>
                </SummaryItem>
                <SummaryItem>
                  <ItemLabel>
                    {creditData.messageChannel === 'SMS'
                      ? 'Phone Number'
                      : 'Device'}
                  </ItemLabel>
                  <ItemValue>
                    {creditData.messageChannel === 'SMS'
                      ? creditData.customerPhone
                      : 'Mobile App'}
                  </ItemValue>
                </SummaryItem>
                <SummaryItem>
                  <ItemLabel>Delivery</ItemLabel>
                  <ItemValue>
                    {creditData.scheduledDateTime
                      ? new Date(creditData.scheduledDateTime).toLocaleString()
                      : 'Immediate'}
                  </ItemValue>
                </SummaryItem>
                <SummaryItem>
                  <ItemLabel>Character Count</ItemLabel>
                  <ItemValue>
                    {characterCount} / {characterLimit}
                  </ItemValue>
                </SummaryItem>
              </SummaryList>
            </SummarySection>
          )}
        </LeftColumn>

        {creditData.sendMessage && (
          <PreviewSection
            title={
              <>
                <svg
                  width="16"
                  height="16"
                  fill={theme?.colors?.primary || '#fa4616'}
                  viewBox="0 0 16 16"
                  style={{ marginRight: '8px' }}
                >
                  <path d="M10.5 8a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0z" />
                  <path d="M0 8s3-5.5 8-5.5S16 8 16 8s-3 5.5-8 5.5S0 8 0 8zm8 3.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z" />
                </svg>
                Message Preview
              </>
            }
          >
            <MessagePreview
              channel={creditData.messageChannel}
              message={replaceVariables(creditData.messageContent)}
              pushTitle={replaceVariables(creditData.messageTitle)}
              showTitle={false}
              compact={true}
            />
          </PreviewSection>
        )}
      </TwoColumnLayout>
    </ReviewContainer>
  );
};

Step3Review.propTypes = {
  creditData: PropTypes.object.isRequired,
  trackName: PropTypes.string,
  transactionData: PropTypes.object,
  type: PropTypes.oneOf(['credit', 'refund']),
};

export default Step3Review;
